import React from 'react';
import { Box, Card, CardContent, Chip, styled, Typography, useTheme } from '@mui/material';
import { useSelectedCommentDecisionQuery } from '@tymely/services';
import { IActionExecution } from '@tymely/atoms';
import { TimelineList } from '@tymely/components';

const _Root = styled(Card)`
    overflow: unset;
    border-radius: 0;
    padding: ${({ theme }) => theme.spacing(1)};
    background: ${({ theme }) => theme.palette.secondary.main};
    .MuiTypography-root {
        color: ${({ theme }) => theme.palette.background.paper};
    }
    .MuiSvgIcon-root {
        color: ${({ theme }) => theme.palette.background.paper};
    }
`;

const _CardContent = styled(CardContent)`
    padding: ${({ theme }) => theme.spacing(0, 0)};
    padding-bottom: 0 !important;
`;

const _Label = styled(Typography)`
    margin: ${({ theme }) => theme.spacing(1, 0)};
`;

const DecisionChip = styled(Chip, { shouldForwardProp: (prop: string) => !['isLive'].includes(prop) })<{
    isLive: boolean;
}>`
    margin-top: ${({ theme }) => theme.spacing(0.5)};
    font-size: 0.8rem;
    background-color: ${({ isLive, theme }) => (isLive ? theme.palette.primary.main : theme.palette.warning.main)};
    border: none;
    color: #fff;
`;

const prepareActions = (actions: IActionExecution[]) => {
    return actions
        .sort((a, b) => a.order - b.order)
        .map((action) => `${action.action_metadata.title} (${action.status})`);
};

type DecisionProps = {
    version?: string;
};

const QaDecision = ({ version }: DecisionProps) => {
    const theme = useTheme();
    const decisionQuery = useSelectedCommentDecisionQuery(!version);
    const decision = decisionQuery.data;

    const titles = React.useMemo(() => {
        if (!decision?.action_executions) {
            return [];
        }
        return prepareActions(decision?.action_executions);
    }, [decision]);

    if (!decision) {
        return null;
    }

    return (
        <_Root>
            <_CardContent>
                <Box sx={{ p: 1 }}>
                    <Box
                        sx={{
                            mt: 1,
                            mb: 1,
                            display: 'flex',
                            justifyItems: 'center',
                        }}
                    >
                        <Typography variant="h6" sx={{ display: 'inline', mr: 1 }}>
                            {decision.title || 'No decision'}
                        </Typography>
                        <Chip
                            label={decision.execution_status}
                            size="small"
                            variant="filled"
                            sx={{
                                marginTop: theme.spacing(0.5),
                                fontSize: theme.typography.body2.fontSize,
                            }}
                        />
                        <DecisionChip
                            label={decision.status}
                            size="small"
                            isLive={decision.status === 'LIVE'}
                            variant="filled"
                        />
                    </Box>
                    <_Label>Workflow {decision.workflow_id}:</_Label>
                    <Box sx={{ height: 8 }} />
                    {!!titles.length && <TimelineList id="comment-actions" items={titles} />}
                </Box>
            </_CardContent>
        </_Root>
    );
};

export default QaDecision;
